import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { Product } from '../lib/types'
import { DEFAULT_PRODUCT, pagePath, productFromSlug, type PageSlug } from '../lib/routes'

/** Whether a product-tab click replaces the current history entry instead of
 * pushing a new one. `false` = push, so Back returns to the previous tab
 * (spec §1-ctrl-route: a tab view "is linkable and survives back and
 * forward"; decided by the PO 2026-10-07, MS-818 OQ-2). The prototype's own
 * tab click replaces — a recorded deviation, see ARCHITECTURE_DECISIONS.md
 * §40. Kept as one exported constant so the tests bind to it. */
export const TAB_CLICK_REPLACES = false

/** The Mambu MCP / Mambu Agents tab of a tabbed page, read from and written
 * to the route's `:tab` segment (`/{page}/{tab}`) instead of component state,
 * so a refresh, a bookmark and Back/Forward all restore it.
 *
 * `ProductTabRoute` only renders a page for a known tab slug (an unknown one
 * gets the in-page not-found state), so the `DEFAULT_PRODUCT` fallback here
 * only matters when a page is mounted outside that route — e.g. a component
 * test rendering it at a bare path. The page element stays mounted across a
 * `:tab` change (same route match), so page-local state behaves as it did
 * when the tab was `useState`. */
export function useProductTab(page: PageSlug): {
  product: Product
  setProduct: (p: Product) => void
} {
  const { tab } = useParams()
  const navigate = useNavigate()
  const product = productFromSlug(tab) ?? DEFAULT_PRODUCT
  const setProduct = useCallback(
    (p: Product) => {
      // Re-clicking the open tab must not stack a duplicate history entry.
      if (productFromSlug(tab) === p) return
      navigate(pagePath(page, p), { replace: TAB_CLICK_REPLACES })
    },
    [navigate, page, tab]
  )
  return { product, setProduct }
}
